'use client';

import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { TenantDto, UpdateTenantRequest } from '../api/tenants';
import { Button } from '@/components/common/Button';
import FormInput from '@/components/common/form/FormInput';
import FormTextArea from '@/components/common/form/FormTextArea';
import Select from '@/components/common/Select';

interface TenantEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: UpdateTenantRequest) => void;
  tenant: TenantDto | null;
}

export const TenantEditModal: React.FC<TenantEditModalProps> = ({ isOpen, onClose, onSave, tenant }) => {
  const [name, setName] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState<UpdateTenantRequest['subscription_status']>('trialing');
  const [settings, setSettings] = useState('');
  const [settingsError, setSettingsError] = useState<string | null>(null);

  const subscriptionOptions = [
    { value: 'trialing', label: 'Trialing' },
    { value: 'active', label: 'Active' },
    { value: 'past_due', label: 'Past Due' },
    { value: 'canceled', label: 'Canceled' },
  ];
  
  useEffect(() => {
    if (tenant) {
      setName(tenant.name);
      setSubscriptionStatus(tenant.subscription_status);
      try {
        const parsedSettings = JSON.parse(tenant.settings || '{}');
        setSettings(JSON.stringify(parsedSettings, null, 2));
      } catch (e) {
        setSettings(tenant.settings || '{}');
      }
      setSettingsError(null);
    }
  }, [tenant]);

  const handleSave = () => {
    try {
      JSON.parse(settings);
      onSave({ name, subscription_status: subscriptionStatus, settings });
      setSettingsError(null);
    } catch (e) {
      setSettingsError('Invalid JSON format.');
    }
  };

  if (!isOpen || !tenant) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-lg w-full rounded-lg bg-white p-6">
          <Dialog.Title className="text-lg font-medium text-gray-900">
            Edit Tenant: {tenant.name}
          </Dialog.Title>

          <div className="mt-4 space-y-4">
            <div>
              <label htmlFor="tenant-name" className="block text-sm font-medium text-gray-700">Tenant Name</label>
              <FormInput
                id="tenant-name"
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="subscription-status" className="block text-sm font-medium text-gray-700">Subscription Status</label>
              <Select
                id="subscription-status"
                value={subscriptionStatus}
                onChange={(value) => setSubscriptionStatus(value as UpdateTenantRequest['subscription_status'])}
                options={subscriptionOptions}
              />
            </div>
            <div>
              <label htmlFor="tenant-settings" className="block text-sm font-medium text-gray-700">Settings (JSON)</label>
              <FormTextArea
                id="tenant-settings"
                rows={8}
                value={settings}
                onChange={(e) => setSettings(e.target.value)}
                className={settingsError ? 'border-red-500 ring-red-500' : ''}
              />
              {settingsError && <p className="mt-1 text-sm text-red-600">{settingsError}</p>}
            </div>
                      </div>

          <div className="mt-6 flex justify-end space-x-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave}>Save Changes</Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
